import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function SendPointsModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation('app');
  const [recipientId, setRecipientId] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!recipientId || !recipientId.trim()) {
      setError(t('sendPointsModal.recipientRequired'));
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError(t('sendPointsModal.amountInvalid'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/transfer-points`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            recipient_wellis_id: recipientId,
            amount: parseFloat(amount),
            notes: notes || null,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'SELF_TRANSFER') {
          setError(t('sendPointsModal.selfTransferError'));
        } else if (data.error === 'INVALID_RECIPIENT') {
          setError(t('sendPointsModal.recipientNotFound'));
        } else if (data.error === 'NETWORK_RESTRICTED') {
          setError(t('sendPointsModal.networkRestricted'));
        } else if (data.error === 'INSUFFICIENT_CREDIT') {
          setError(t('sendPointsModal.insufficientCredit'));
        } else if (data.error === 'INVALID_AMOUNT') {
          setError(t('sendPointsModal.amountInvalid'));
        } else {
          setError(data.message || t('sendPointsModal.error'));
        }
        return;
      }

      setSuccess(true);
      setRecipientId('');
      setAmount('');
      setNotes('');
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.message || t('sendPointsModal.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleReset = () => {
    setRecipientId('');
    setAmount('');
    setNotes('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('sendPointsModal.title')}
          </h2>
        </div>

        <div className="p-6 space-y-4">
          {success ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded text-green-700 text-center">
              {t('sendPointsModal.success')}
            </div>
          ) : (
            <>
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sendPointsModal.recipientWellisId')}
                  </label>
                  <input
                    type="text"
                    value={recipientId}
                    onChange={(e) => setRecipientId(e.target.value)}
                    placeholder="WELLIS-12345678"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('sendPointsModal.recipientHelp')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sendPointsModal.creditAmount')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="100.00"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sendPointsModal.notes')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('sendPointsModal.notesPlaceholder')}
                    disabled={loading}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
                >
                  {t('sendPointsModal.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !recipientId || !amount || parseFloat(amount) <= 0}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {loading ? t('sendPointsModal.sending') : t('sendPointsModal.send')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
